<template>
    <component
        :alert="alert"
        :class="`${visible ? '' : 'hidden'} ${item.props && item.props.required ? 'required' : ''} ${componentStyles}`"
        :data="data"
        :is="getComponentType()"
        :item="itemData"
        :optional="optional"
        :readonly="disabled"
        :rules="rules"
        :width="width"
        :projectId="projectId"
        :moduleKey="moduleKey"
        :userId="userId"
        ref="component"
        v-model="modelData"
        :handleTabChange="handleTabChange"
        @handleTabChange="handleTabChange"
        :openQuestionnaire="openQuestionnaire"
        @openQuestionnaire="openQuestionnaire"

    />
</template>

<script>
    import Autocomplete from './autocomplete';
    import CheckboxList from './checkbox-list';
    import DataTable from './data-table';
    import ExpandableTable from './expandable-table';
    import DatePicker from './date-picker';
    import InputLabel from './label';
    import NumberedItem from './numbered-item';
    import OrderableList from './orderable-list.vue';
    import RadioList from './radio-list';
    import SelectList from './select-list';
    import Slider from './slider';
    import TextArea from './text-area';
    import TextField from './text-field';
    import { toFloat } from '../utilities';
    import { toDollars } from '../utilities';
    import { checkVisibility } from '../utilities';
    import { DEFAULT_INPUT_WIDTH, EMAIL_REGEX, ZIP_REGEX } from '../constants';
    import formulaHandlerMixins from './mixins/formula-handler-mixins';

    export default {
        name: 'DynamicInput',
        mixins: [formulaHandlerMixins],
        components: {
            Autocomplete,
            CheckboxList,
            DataTable,
            DatePicker,
            ExpandableTable,
            InputLabel,
            NumberedItem,
            OrderableList,
            RadioList,
            SelectList,
            Slider,
            TextArea,
            TextField
        },
        props: {
            data: Object,
            isReadOnly: Boolean,
            item: Object,
            questionnaireData: Object,
            readonly: Boolean,
            validatingSubmission: Boolean,
            value: [String, Number, Array, Object],
            visible: Boolean,
            projectId: String,
            moduleKey: String,
            userId: String,
            isInline: Boolean,
            handleTabChange: Function,
            openQuestionnaire: Function
        },
        computed: {
            modelData: {
                get: function () {
                    const defaultValue = this.item && this.item.props && this.item.props.default;

                    if (this.value === undefined && defaultValue !== undefined) {
                        this.$emit('input', defaultValue);
                    }

                    return this.value;
                },
                set: function (newVal) {
                    const validate = this.$refs.component && this.$refs.component.validate;

                    if (validate) {
                        // Validate the value if the component exists and a validate method is defined
                        validate(newVal);
                    } 
                    // Either the validate method isn't defined or the component doesn't exist. Accept the value
                    // The slider component will trigger the validate method before rendering so we have to trust the value
                    this.$emit('input', newVal);   
                }
            },
            optional: function () {
                return this.item.props && this.item.props.optional;
            },
            width: function () {
                return this.item.width || (this.item.props && this.item.props.width) || DEFAULT_INPUT_WIDTH;
            },
            componentStyles: function () {
                const styles = this.item.props && this.item.props.styles;
                
                if (styles && styles.length) {
                    return styles.map(thisStyle => `dynamicinput-${thisStyle}`).join(' ');
                }

                return '';
            },
            unanswered: function () {
                const { item = {} } = this;
                const { props = {}, name } = item;
                const { optional } = props;

                return this.validatingSubmission && name && (this.modelData === undefined || this.modelData === '') && !optional;
            }
        },
        data: function () {
            const itemData = Object.assign({}, this.item);

            itemData.props = itemData.props || {};

            let label = itemData.props.label;

            if ((itemData.type === 'selectlist' || itemData.type === 'autocomplete') && !label) {
                label = 'Please Select an Option';
            }

            itemData.props.label = label;
            
            if (itemData.type === 'radio-list' && !this.isInline) {
                itemData.props.radioIsInline = false;
            } else if (itemData.type === 'radio-list' && this.isInline) {
                itemData.props.radioIsInline = true;
            } else {
                itemData.props.radioIsInline = null;
            }

            return {
                itemData,
                rules: this.getRules(),
                alert: null,
                disabled: this.readonly
            };
        },
        mounted: function () {
            const self = this;
            const props = this.item && this.item.props || {};
            const { formula, alertRules, isReadOnly } = props;
            // The formula property is used to calculate the value of a property
            // based on the value of other properties.

            function bindHandler ({ handler, targets }) {
                // Call the handler once to set initial values
                // There is an issue where the "formula" components don't get updated on initialize
                // If they are rendered before the "arguments" components
                // Adding a setTimeout wait until all components have rendered before calculating "formula"s
                setTimeout(handler, 0);

                // Using a formula allows a component to be updated
                // by the values from other components
                targets.forEach((thisArg) => {
                    // Listen to changes on the other component attributes
                    // And call the specified handler
                    self.$watch(() => self.data[thisArg], handler);
                });
            }

            if (formula) {
                const { name, dependencies } = formula;
                // arguments is an array of attributes that should update this component
                const args = formula.arguments;
                const handler = this[name].bind(this, args, this.data);
                let targets = args.targets || [];

                if (dependencies) {
                    targets = targets.concat(dependencies.items.map((thisItem) => thisItem.name));
                }

                bindHandler({
                    handler: function () {
                        if(dependencies) {
                            // Check if this formula executes dependent on dependencies
                            const meetsDependencies = checkVisibility({
                                item: formula,
                                model: self.data
                            });

                            if (!meetsDependencies) {
                                // This does not meet the requirements of the dependencies, leave now.
                                return;
                            }
                        }
                        const newValue = handler(props);

                        // Formula is used to calculate the value of a field based on other fields so we need to update the value
                        self.update(self.item.name, newValue);
                    },
                    targets
                });
            }

            if (alertRules) {
                // Coerce to an array if it isn't already
                const arrAlertRules = Array.isArray(alertRules) ? alertRules : [alertRules];

                arrAlertRules.forEach((alertRule) => {
                    const { name, field } = alertRule;
                    const handler = this.generateCustomRule(alertRule);

                    bindHandler({
                        handler: function (newVal) {
                            const newValue = handler(newVal);

                            // The logic for generating the alert is the same as a rule so `true` means there is no warning
                            self.alert = newValue === true ? null: newValue;
                        },
                        // An alert should update when the field being checked against is updated
                        // Be careful of creating infinite loops if combining with a formula
                        targets: alertRule.targets.concat(field)
                    });
                });
            }

            if (isReadOnly) {
                const { dependencies, value } = isReadOnly;
                let targets = [];

                if (dependencies) {
                    targets = targets.concat(dependencies.items.map((thisItem) => thisItem.name));
                }

                bindHandler({
                    handler: function () {
                        let newValue = value;

                        if(dependencies) {
                            // Check if this formula executes dependent on dependencies
                            const meetsDependencies = checkVisibility({
                                item: isReadOnly,
                                model: self.data
                            });

                            if (!meetsDependencies) {
                                newValue = !value;
                            }
                        }

                        self.disabled = newValue;
                    },
                    targets
                });
            }
        },
        methods: {
            checkboxRequiredRule: function (val) {
                const isVisible = this.visible;
                let hasValue = false;

                for (let i in val) {
                    if (val[i] === true) {
                        hasValue = true;
                        break;
                    }
                }

                const isRequired = isVisible && !hasValue ? 'Please Check an Item': true;

                return isRequired;
            },

            dataTableRequiredRule (val) {
                const isVisible = this.visible;

                const isRequired = isVisible && !val ? 'Please Include an Item': true;

                return isRequired;
            },

            /**
             * generates custom rules to apply to an input
             * ruleData.operator {String} How to compare - 'equal'
             * ruleData.message {String} - Error message to display if the rule doesn't resolve
             * ruleData.targets {Array of Strings} - The fields of the questionnaire to compare against
             * ruleData.name {String} - Operation to apply to field values in `arguments` - 'sum'
             *
             * {
             *      "name": "sum",
             *      "targets": ["revenue-totals-transactional", "revenue-totals-recurring"],
             *      "operator": "equal",
             *      "field": "revenue-last-quarter", // Define either `field` or `value`
             *      "value": 100, // Define either `field` or `value`
             *      "message": "Trailing 12-Month Revenue doesn't equal the Revenue Breakdown."
             * }
             *
             */
            generateCustomRule: function (ruleData) {
                const self = this;
                const {
                    defaultValue,
                    field,
                    message,
                    name,
                    operator,
                    value
                } = ruleData;

                let handler;

                if (name && name !== 'currentValue') {
                    handler = this[name].bind(this, ruleData, this.data);
                }

                return function (currentValue) {
                    // If `value` is defined, use that as the value to compare against
                    const fieldValue = typeof value === 'undefined' ? self.questionnaireData[field] || defaultValue : value;
                    // If `currentValue` is defined, use that instead of calculating the value from other fields
                    const val = name === 'currentValue' ? currentValue : handler();

                    switch (operator) {
                        case 'equal': {
                            return fieldValue === val ? true : message;
                        }

                        case 'notEqual': {
                            return fieldValue !== val ? true : message;
                        }

                        case 'greaterThan': {
                            return parseFloat(fieldValue) >= parseFloat(val) ? true : message;
                        }

                        case 'lessThan': {
                            return parseFloat(fieldValue) <= parseFloat(val) ? true : message;
                        }

                        case 'isNegative': {
                            return 0 <= parseFloat(val) ? true : message;
                        }

                        case 'includedIn': {
                            if (!Array.isArray(fieldValue)) {
                                throw new Error('fieldValue must be an array for includedIn rules');
                            }

                            return value.contains(fieldValue) ? true : message;
                        }
                        
                        case 'notIncludedIn': {
                            if (!Array.isArray(fieldValue)) {
                                throw new Error('fieldValue must be an array for includedIn rules');
                            }

                            return value.contains(fieldValue) ? message : true;
                        }
                    }
                }
            },
            getRules: function () {
                const rules = [];
                const numberFormats = [
                    'dollars',
                    'integer',
                    'percent',
                    'number'
                ];
                const props = this.item.props || {};
                const { format, customRules, required, maxItems, minLength } = props;

                if (required) {
                    if (this.item.type === 'data-table' || this.item.type === 'expandable-table') {
                        rules.push(this.dataTableRequiredRule);
                    } else if (this.item.type === 'checkbox-list') {
                        rules.push(this.checkboxRequiredRule);
                    } else {
                        rules.push(this.requiredRule);
                    }
                }

                if (numberFormats.includes(format)) {
                    rules.push(this.numberRule);
                }

                if (format === 'email') {
                    rules.push(this.emailRule);
                }

                if (format === 'zip') {
                    rules.push(this.zipRule);
                }

                if (format === 'phone') {
                    rules.push(this.phoneRule);
                }

                if (format === 'percent') {
                    rules.push(this.percentRule);
                }

                if (format === 'multiples') {
                    rules.push(this.multiplesRule);
                }

                if (format === 'integer') {
                    rules.push(this.integerRule);
                }

                if ((this.item.type !== 'data-table' || this.item.type !== 'expandable-table') && maxItems) {
                    rules.push(this.maxItemsRule);
                }

                if ( minLength ) {
                    rules.push(this.minLengthRule);
                }

                // if ( format === 'url' ) {
                //     rules.push(this.urlCheck);
                // }

                if (customRules && customRules.length) {
                    customRules.forEach(thisRule => rules.push(this.generateCustomRule(thisRule)));
                }

                return rules;
            },
            wordCountRule: function (val) {
                return val.trim().split(' ').length <= 5 ? true : 'Max 5 words'
            },
            minLengthRule: function ( val ) {
                if ( !val ) return `Minimum length is ${this.item.props.minLength}`

                let clean_val = val.toString()
                if ( this.item.props.format == 'dollars' ) {
                    clean_val = clean_val.split('.')[0]
                }

                return clean_val.length >= this.item.props.minLength ? true : `Minimum length is ${this.item.props.minLength}`
            },
            emailRule: function (val) {
                const isVisible = this.visible;

                if (!val || !isVisible) {
                    return true;
                }

                return val.match(EMAIL_REGEX) ? true : 'Please enter a valid email address';
            },
            integerRule: function (val = '') {
                const isVisible = this.visible;

                if (!isVisible) {
                    return true;
                }

                if (val.indexOf('.') >= 0) {
                    return 'Please enter a whole number';
                }
                
                return true;
            },
            multiplesRule: function (val) {
                const isVisible = this.visible;

                if (!isVisible) {
                    return true;
                }

                let clean_val = val.toString()
                clean_val = clean_val.split('.')[0]
 
                if (clean_val.length > 2) {
                    return 'Only 2 digits allowed';
                }
                
                return true;
            },
            maxItemsRule: function (val) {
                const isVisible = this.visible;
                const { maxItems } = this.item.props;

                if (!val || !isVisible) {
                    return true;
                }
                if (!Array.isArray(val)) {
                    return true;
                }
                if (val.length <= parseInt(maxItems, 10)) {
                    return true;
                }

                return `Please select no more than ${maxItems} items.`;
            },
            numberRule: function (val) {
                const isVisible = this.visible;

                if (!val || !isVisible) {
                    return true;
                } else if (typeof val === 'number') {
                    return true;
                } else if (val && val.match(/^[0-9,.-]+$/)) {
                    return true;
                } else {
                    return 'Please enter a number';
                }
            },
            percentRule: function (val = '') {
                if ( typeof val !== 'string' ) val = ''
                const isVisible = this.visible;

                if (!isVisible) {
                    return true;
                }

                if ( typeof val !== 'string' ) {
                    val = val != '' ? val.toString() : '';
                }
                const numValue = parseFloat(val.replace(/,/g, ''), 10);

                if (!isNaN(numValue) && numValue > 100) {
                    return 'Please enter a value not greater than 100';
                }
                
                return true;
            },
            phoneRule: function (val = '') {
                const isVisible = this.visible;

                if (!isVisible) {
                    return true;
                }

                const phoneVal = val.replace(/[^0-9]/g, '');

                return phoneVal.length === 10 ? true : 'Please enter a phone number including area code';
            },
            requiredRule: function (val) {
                const isVisible = this.visible;
                const hasValue = typeof val !== 'undefined' && val !== '' && val !== null;
                const isRequired = isVisible && !hasValue ? 'This field is required': true;

                return isRequired;
            },
            zipRule: function (val) {
                if (!val) {
                    return true;
                }

                return val.match(ZIP_REGEX) ? true : 'Please enter a valid ZIP Code';
            },
            getComponentType: function (type) {
                type = type || this.item.type;

                const typeToComponentMap = {
                    'autocomplete': 'Autocomplete',
                    'checkbox-list': 'CheckboxList',
                    'data-table': 'DataTable',
                    'datepicker': 'DatePicker',
                    'expandable-table': 'ExpandableTable',
                    'input-label': 'InputLabel',
                    'orderable-list': 'OrderableList',
                    'radio-list': 'RadioList',
                    'selectlist': 'SelectList',
                    'slider': 'Slider',
                    'text-field': 'TextField',
                    'textarea': 'TextArea'
                }

                const componentType = typeToComponentMap[type];

                return componentType;
            },
            naCheck: function (args, modelData) {
                const { targets } = args;

                const values = targets.map( thisArg => thisArg.value || modelData[thisArg] );

                return values[0] == '' ? 'NA' : values[0];
            },
            handleNumericOther: function (args, modelData) {
                const { targets } = args;

                const values = targets.map( thisArg => thisArg.value || modelData[thisArg] );

                return values[0] == 'other' ? values[1] : values[0];
            },
            switch: function (args, modelData, props) {
                const value = modelData[args.targets[0]];
                const { data } = props.formula;
                
                return data && (data[value] || data.default);
            },
            dateformat: function (args, modelData) {
                const dateData = modelData[args.targets[0]];
                if (!dateData) {
                    return undefined;
                }

                const date = new Date(dateData);
                const dateTimeFormat = new Intl.DateTimeFormat('en', {
                    year: 'numeric',
                    month: 'short',
                    day: '2-digit',
                    timeZone: 'UTC'
                });
                const [{ value: month },,{ value: day },,{ value: year }] = dateTimeFormat.formatToParts(date);
                const dateValues = {
                    month,
                    day,
                    year
                };

                return dateValues[args.value];
            },
            // Simply retrn the value of another field for comparisons
            valueOf: function (args, modelData) {
                const target = args.targets && args.targets[0];
                const val = target && target.value || modelData[target];
                    
                return val;
            },
            percent: function (args, modelData) {
                const { value, percent } = args;
                const formattedValue = toFloat(modelData[value]);
                const formattedPercent = toFloat(modelData[percent]);
                const percentage = formattedValue * formattedPercent / 100;

                return percentage;
            },
            /* Calculate the percent of a value
                "formula": "list",
                "arguments": {
                    "targets": [
                        "listens-to-fields"
                    ],
                    "total": "field_with_total_value",  // total is the divisor in the equation, can be defined as a field in the module
                    "total": {                          // total can also optionally be a static value
                        "value": 100
                    }
                    "value": "field_with_value_value",  // value is the dividend  in the equation, can also be defined as a field in the module
                    "value": {                          // value can also optionally be a static value
                        "value": 25
                    }
                }
            */
            percentOf: function (args, modelData) {
                const { value, total } = args;
                const formattedValue = toFloat(value && value.value || modelData[value]);
                const formattedTotal = toFloat(total && total.value || modelData[total]);
                const percentage = formattedValue / formattedTotal * 100;

                return `${isNaN(percentage) ? '' : percentage}`;
            },
            average: function (args, modelData) {
                const { targets } = args;
                const values = targets.map( thisArg => thisArg.value || modelData[thisArg] );
                
                const sum = values.reduce((acc, num) => acc + parseInt(num), 0);
                const average = sum / values.length;
                
                return average;
            },
            update: function (name, value) {
                // This will update the entire values object,
                // Causing a rerender of any affected components
                // TODO: I feel like there must be a better way to do this
                // but I'm struggling to find it.
                // const newValue = Object.assign({}, this.data, {
                //     [name]: '' + value
                // });

                // this.$emit('input', newValue);
                this.$emit('input', value);
            }
        },
        watch: {
            visible: function () {
                if (this.$refs.component && this.$refs.component.validate) {
                    this.$refs.component.validate();
                }
            },
            unanswered: {
                handler: function (newVal, oldVal) {
                    if (newVal !== oldVal) {
                        this.item.unanswered = newVal;
                        // Tell the parent that this item unanswered value has changed
                        // This will allow the parent to update the unanswered count
                        this.$emit('unanswered', newVal);
                    }
                },
                immediate: true
            }
        }
    }
</script>

<style>
    .required .v-text-field__slot > label::after,
    .required .v-select__slot > label::after,
    .required .iterable-input-subitem .v-input--radio-group__input::after {
        content: "*";
        color: #ff0000;
    }
    .dynamicinput-total {
        border-top: 1px solid rgba(0, 0, 0, 0.19);
    }
</style>


// WEBPACK FOOTER //
// src/components/questionnaire_template/dynamic-input.vue